import { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// eslint-disable-next-line react/prop-types
export const CustomThemeProvider = ({ children }) => {

  const [darkMode, setDarkMode] = useState(() => {
    // Get saved theme from localStorage or default to false (light mode)
    const savedTheme = localStorage.getItem('darkMode');
    return savedTheme ? JSON.parse(savedTheme) : false;
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: darkMode ? '#5865f2' : '#1976d2', // Discord blue
      },
      secondary: {
        main: darkMode ? '#57f287' : '#dc004e', // Discord green
      },
      background: {
        default: darkMode ? '#36393f' : '#fafafa', // Discord dark gray
        paper: darkMode ? '#2f3136' : '#ffffff', // Discord darker gray
      },
      text: {
        primary: darkMode ? '#dcddde' : '#000000', // Discord light gray text
        secondary: darkMode ? '#96989d' : '#666666', // Discord muted text
      },
      divider: darkMode ? '#40444b' : 'rgba(0, 0, 0, 0.12)',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: darkMode ? '#36393f !important' : '#fafafa !important',
            margin: 0,
            padding: 0,
            minHeight: '100vh',
          },
          html: {
            backgroundColor: darkMode ? '#36393f !important' : '#fafafa !important',
            minHeight: '100vh',
          },
          '#root': {
            backgroundColor: darkMode ? '#36393f !important' : '#fafafa !important',
            minHeight: '100vh',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#202225' : '#1976d2', // Discord almost black
            borderBottom: darkMode ? '1px solid #40444b' : 'none',
            color: darkMode ? '#ffffff' : '#ffffff',
            '& .MuiToolbar-root': {
              color: '#ffffff !important',
            },
            '& .MuiButton-root': {
              color: '#ffffff !important',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
              },
            },
            '& .MuiIconButton-root': {
              color: '#ffffff !important',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
              },
            },
            '& .MuiTypography-root': {
              color: '#ffffff !important',
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: darkMode ? '#202225' : '#1976d2',
              color: darkMode ? '#dcddde' : '#ffffff',
              borderBottom: darkMode ? '1px solid #40444b' : 'none',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: darkMode ? '1px solid #40444b' : '1px solid rgba(224, 224, 224, 1)',
            backgroundColor: darkMode ? '#2f3136' : 'inherit',
            color: darkMode ? '#dcddde' : 'inherit',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: darkMode ? '#32353b' : 'rgba(0, 0, 0, 0.04)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#2f3136' : '#ffffff',
            border: darkMode ? '1px solid #40444b' : 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: darkMode ? '#2f3136' : '#ffffff',
            border: darkMode ? '1px solid #40444b' : 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: darkMode ? '#40444b' : 'inherit',
              '& fieldset': {
                borderColor: darkMode ? '#40444b' : 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: darkMode ? '#5865f2' : 'rgba(0, 0, 0, 0.87)',
              },
            },
            '& .MuiInputLabel-root': {
              color: darkMode ? '#96989d' : 'rgba(0, 0, 0, 0.6)',
            },
            '& .MuiOutlinedInput-input': {
              color: darkMode ? '#dcddde' : 'inherit',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            '&.MuiButton-contained': {
              backgroundColor: darkMode ? '#5865f2' : '#1976d2',
              color: '#ffffff',
              '&:hover': {
                backgroundColor: darkMode ? '#4752c4' : '#115293',
              },
              '&.Mui-disabled': {
                backgroundColor: darkMode ? '#40444b' : '#e0e0e0',
                color: darkMode ? '#72767d' : '#9e9e9e',
                opacity: 0.6,
              },
            },
            '&.MuiButton-outlined': {
              borderColor: darkMode ? '#40444b' : 'rgba(25, 118, 210, 0.5)',
              color: darkMode ? '#dcddde' : '#1976d2',
              '&:hover': {
                borderColor: darkMode ? '#5865f2' : '#1976d2',
                backgroundColor: darkMode ? 'rgba(88, 101, 242, 0.04)' : 'rgba(25, 118, 210, 0.04)',
              },
              '&.Mui-disabled': {
                borderColor: darkMode ? '#40444b' : '#e0e0e0',
                color: darkMode ? '#72767d' : '#9e9e9e',
                opacity: 0.6,
              },
            },
            '&.MuiButton-text': {
              color: darkMode ? '#dcddde' : '#1976d2',
              '&:hover': {
                backgroundColor: darkMode ? 'rgba(220, 221, 222, 0.04)' : 'rgba(25, 118, 210, 0.04)',
              },
              '&.Mui-disabled': {
                color: darkMode ? '#72767d' : '#9e9e9e',
                opacity: 0.6,
              },
            },
          },
        },
      },
    },
  });

  const value = {
    darkMode,
    toggleDarkMode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};